import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Check, ArrowLeft, CreditCard } from 'lucide-react';
import { BASE_URL } from '../config';

const PackageDetails = () => {
  const [step, setStep] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [currentPlan, setCurrentPlan] = useState('');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all packages from API
        const packagesRes = await axios.get(`${BASE_URL}/api/packages/all/`);
        setPackages(packagesRes.data);

        // Fetch current plan
        const hospitalId = localStorage.getItem('hospitalId');
        const currentRes = await axios.get(
          `${BASE_URL}/api/superadmin/${hospitalId}/package/current/`
        );
        setCurrentPlan(currentRes.data.package_name);
      } catch (err) {
        console.error('❌ Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handlePayment = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const hospitalId = localStorage.getItem('hospitalId');

      const res = await axios.put(
        `${BASE_URL}/api/superadmin/${hospitalId}/package/`,
        { package_id: selectedPackage },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      alert(
        `Payment successful! Your plan has been upgraded to ${res.data.package_name}`
      );

      const refreshed = await axios.get(
        `${BASE_URL}/api/superadmin/${hospitalId}/package/current/`
      );
      setCurrentPlan(refreshed.data.package_name);
      setStep(1);
    } catch (err) {
      console.error('❌ Error updating hospital package:', err);
      alert('Failed to update package');
    }
  };

  const getSelectedPackageDetails = () => {
    return packages.find((pkg) => pkg.package_ID === selectedPackage);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading packages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <AnimatePresence mode="wait">
        {/* Step 1: Select Package */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                Upgrade Your Plan
              </h1>
              <p className="text-slate-600">
                Select a plan that best fits your needs
              </p>
            </div>

            <div className="overflow-x-auto pb-4 mb-8">
              <div className="flex gap-6 min-w-max px-2">
              {packages.map((pkg) => {
  const isCurrent = pkg.package_name === currentPlan;
  const detailsArray = pkg.details ? pkg.details.split('\n').filter(d => d.trim()) : [];
  
  return (
    <motion.div
      key={pkg.package_ID}
      whileHover={{ scale: 1.02 }}
      onClick={() => setSelectedPackage(pkg.package_ID)}
      className={`relative bg-white rounded-xl shadow-sm p-6 flex flex-col w-80 flex-shrink-0 transition-all cursor-pointer ${
        isCurrent
          ? 'ring-2 ring-green-500/50 shadow-lg'
          : selectedPackage === pkg.package_ID
          ? 'ring-2 ring-medical-500 shadow-lg'
          : 'hover:shadow-md'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-slate-900">{pkg.package_name}</h3>
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
            selectedPackage === pkg.package_ID
              ? 'border-medical-500 bg-primary-500/100'
              : 'border-slate-300'
          }`}
        >
          {selectedPackage === pkg.package_ID && (
            <Check className="w-3 h-3 text-slate-900" />
          )}
        </div>
      </div>

      <div className="mb-4">
        <span className="text-3xl font-bold text-slate-900">${pkg.price}</span>
        <span className="text-slate-600 text-sm">/month</span>
      </div>

      <div className="space-y-2 mb-4 flex-grow">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Check className="w-4 h-4 text-medical-500 flex-shrink-0" />
          <span>{pkg.credit_limit} Credits</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Check className="w-4 h-4 text-medical-500 flex-shrink-0" />
          <span>{pkg.days_limit} Days</span>
        </div>
        
        {detailsArray.length > 0 && (
          <div className="pt-2 mt-2 border-t border-slate-200">
            {detailsArray.map((detail, index) => (
              <div key={index} className="flex items-start gap-2 text-sm text-slate-600 mb-1">
                <Check className="w-4 h-4 text-medical-500 flex-shrink-0 mt-0.5" />
                <span>{detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-auto">
        {isCurrent ? (
          <div className="space-y-2">
            <div className="w-full py-2 bg-green-500/20 border border-green-500 text-green-400 font-medium rounded-lg text-center">
              Current Plan
            </div>
            <button
              onClick={() => setSelectedPackage(pkg.package_ID)}
              className="w-full py-2 bg-primary-500/100 hover:bg-medical-600 text-white font-medium rounded-lg"
            >
              Renew Plan
            </button>
          </div>
        ) : (
          <button
            onClick={() => setSelectedPackage(pkg.package_ID)}
            className="w-full py-2 bg-primary-500/100 hover:bg-medical-600 text-white font-medium rounded-lg"
          >
            Select Plan
          </button>
        )}
      </div>
    </motion.div>
  );
})}
              </div>
            </div>

            <div className="flex justify-center gap-4">
              {/* <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 px-6 py-3 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button> */}
              <button
                onClick={() =>
                  selectedPackage ? setStep(2) : alert('Please select a package')
                }
                className="px-6 py-3 bg-primary-500/100 hover:bg-medical-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                disabled={!selectedPackage}
              >
                Continue to Payment
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex items-center justify-center min-h-[500px]"
          >
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl w-full">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500/20 rounded-full mb-4">
                  <CreditCard className="w-8 h-8 text-primary-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Payment Summary
                </h2>
              </div>

                            <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                  <span className="text-slate-600">Selected Plan:</span>
                  <span className="font-semibold text-slate-900">
                    {getSelectedPackageDetails()?.package_name}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                  <span className="text-slate-600">Credits:</span>
                  <span className="font-semibold text-slate-900">{getSelectedPackageDetails()?.credit_limit}</span>
                </div>

                <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                  <span className="text-slate-600">Duration:</span>
                  <span className="font-semibold text-slate-900">{getSelectedPackageDetails()?.days_limit} Days</span>
                </div>

                <div className="flex justify-between items-center p-4 bg-primary-500/10 rounded-lg">
                  <span className="text-slate-700 font-medium">Amount:</span>
                  <span className="text-2xl font-bold text-primary-400">
                    ${getSelectedPackageDetails()?.price}
                  </span>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  onClick={handlePayment}
                  className="px-5 py-2 bg-primary-500/100 hover:bg-medical-600 text-white font-medium rounded-lg transition-colors"
                >
                  Confirm Payment
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PackageDetails;
